import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../constants/api';
import './Modal.css';

const Modal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    restaurantType: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling when modal is closed
      document.body.style.overflow = 'unset';
      // Clear form fields and errors when modal is closed
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        restaurantType: ''
      });
      setErrors({});
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.firstName.trim() === '') {
      newErrors.firstName = 'First name is required';
    }
    if (formData.lastName.trim() === '') {
      newErrors.lastName = 'Last name is required';
    }
    if (formData.email.trim() === '') {
      newErrors.email = 'Email is required';
    }
    if (formData.restaurantType === '') {
      newErrors.restaurantType = 'Restaurant type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields (all except phone)
    if (!validateForm()) {
      return;
    }
    
    // Handle form submission here
    console.log('Form submitted:', formData);
    
    // Save minimal user info locally for later use
    try {
      localStorage.setItem('leadInfo', JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        restaurantType: formData.restaurantType
      }));
    } catch {}

    // POST to backend to register lead start - wait for success before navigating
    try {
      const url = `${API_BASE_URL}/api/Leads/start`;
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || "",
        restaurantType: formData.restaurantType,
        source: 'questionnaire',
        createdAt: new Date().toISOString(),
        leadStatus: "COLD" 
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        console.error('Lead start POST failed');
        // Still allow navigation even if API fails
      } else {
        const result = await response.json();
        console.log('Lead created successfully:', result);
      }
    } catch (err) {
      console.error('Error preparing lead start POST', err);
      // Still allow navigation even if API fails
    }
    
    // Store email in localStorage for results page
    if (formData.email) {
      localStorage.setItem('userEmail', formData.email);
    }
    
    try {
      // Navigate to questionnaire page
      navigate('/questionnaire');
      console.log('Navigation called');
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback to window.location if navigate fails
      window.location.href = '/questionnaire';
    }
    
    // Close modal after navigation
    onClose();
  };

  return (
    <div className={`modal-overlay ${isOpen ? 'modal-open' : 'modal-closed'}`} onClick={onClose} style={{ display: isOpen ? 'flex' : 'none' }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          ×
        </button>
        
        <p className="modal-title">
          Enter your details below to start the assessment
        </p>
        
        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-field">
              <input
                type="text"
                name="firstName"
                placeholder="First name *"
                value={formData.firstName}
                onChange={handleInputChange}
                className={errors.firstName ? 'error' : ''}
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>
            <div className="form-field">
              <input
                type="text"
                name="lastName"
                placeholder="Last name *"
                value={formData.lastName}
                onChange={handleInputChange}
                className={errors.lastName ? 'error' : ''}
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>
          
          <div className="form-field">
            <input
              type="email"
              name="email"
              placeholder="Email *"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>
          
          <div className="form-field">
            <select
              name="restaurantType"
              value={formData.restaurantType}
              onChange={handleInputChange}
              className={`restaurant-type-select ${formData.restaurantType ? 'has-value' : ''} ${errors.restaurantType ? 'error' : ''}`}
            >
              <option value="">Restaurant Type *</option>
              <option value="Pizzeria">Pizzeria</option>
              <option value="Deli">Deli</option>
              <option value="Chinese">Chinese</option>
              <option value="American">American</option>
              <option value="Mexican">Mexican</option>
              <option value="Indian">Indian</option>
              <option value="Thai">Thai</option>
              <option value="Japanese">Japanese</option>
            </select>
            {errors.restaurantType && <span className="error-message">{errors.restaurantType}</span>}
          </div>
          
          <div className="form-field phone-field">
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number (optional)"
              value={formData.phone}
              onChange={handleInputChange}
            />
          </div>
          
          <button
            type="submit"
            className="modal-submit-btn"
          >
            Start
          </button>
        </form>
        
        <div className="modal-disclaimer">
          <p>
            Your personalised results will be emailed to you. You can opt out at any time.
          </p>
          <Link to="#" className="privacy-link">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default Modal;
